import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";
import { ListChecks, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, Check, X, Minus, ArrowRight } from "@phosphor-icons/react";

const fmt = (n) => (!n && n !== 0) ? "$0" : `$${Number(n).toLocaleString()}`;

const piaColors = {
  not_started: "bg-gray-100 text-[#6B7280] border-gray-200",
  in_review: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  info_requested: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};
const piaLabels = { not_started: "Not Started", in_review: "In Review", info_requested: "Info Req.", approved: "Approved", denied: "Denied" };
const statusColors = {
  draft: "bg-gray-100 text-[#6B7280] border-gray-200",
  submitted: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  committed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
  pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
};
const stageLabels = { draft: "Draft", submitted: "Submitted", funded: "Funded", service_confirmed: "486 Filed", invoiced: "Invoiced" };
const stageColors = { draft: "#6B7280", submitted: "#002FA7", funded: "#00B341", service_confirmed: "#0891B2", invoiced: "#D97706" };

const FiledIcon = ({ filed }) => filed
  ? <div className="w-5 h-5 bg-[#00B341]/10 border border-[#00B341]/20 flex items-center justify-center"><Check size={10} className="text-[#00B341]" weight="bold" /></div>
  : <div className="w-5 h-5 bg-gray-50 border border-gray-200 flex items-center justify-center"><Minus size={10} className="text-gray-300" /></div>;

export default function FRNStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/frn-status").then((r) => setData(r.data)).catch(() => toast.error("Failed to load FRN status")).finally(() => setLoading(false));
  }, []);

  const items = data?.rows || [];
  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);

  const csvCols = [
    { label: "FRN", key: "frn" }, { label: "Client", key: "client_name" }, { label: "FY", key: "funding_year" },
    { label: "Provider", key: "service_provider" }, { label: "Category", key: "category" },
    { label: "Requested", key: "requested" }, { label: "471 Status", key: "status_471" }, { label: "PIA", key: "pia_status" },
    { label: "486 Filed", accessor: (r) => r.has_486 ? "Yes" : "No", key: "has_486" },
    { label: "Invoiced", key: "total_invoiced" }, { label: "Disbursed", key: "total_disbursed" },
    { label: "% Paid", key: "pct_disbursed" }, { label: "Stage", key: "stage" },
  ];

  const SH = ({ k, children, className = "" }) => (
    <TableHead className={`py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20] ${className}`} onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-sm text-[#6B7280]">Loading FRN status...</p></div>;

  const summary = data?.summary || {};

  return (
    <div className="p-6" data-testid="frn-status-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">E-Rate Forms</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FRN Status</h1>
          <p className="text-sm text-[#6B7280] mt-1">Comprehensive status of all FRNs across E-Rate forms</p>
        </div>
        <button data-testid="export-frn-status-btn" onClick={() => exportCSV(processed, csvCols, "frn_status.csv")} className="flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      {/* Pipeline stages */}
      <div className="grid grid-cols-5 gap-0 border border-gray-200 mb-6">
        {[
          { key: "draft", label: "Draft", count: summary.draft },
          { key: "submitted", label: "Submitted", count: summary.submitted },
          { key: "funded", label: "Funded (FCDL)", count: summary.funded },
          { key: "service_confirmed", label: "486 Filed", count: summary.service_confirmed },
          { key: "invoiced", label: "Invoiced", count: summary.invoiced },
        ].map((s, i) => (
          <button key={s.key} data-testid={`stage-${s.key}`}
            onClick={() => setFilter("stage", filters.stage === s.key ? "all" : s.key)}
            className={`p-4 border-r border-gray-200 last:border-r-0 text-left transition-colors ${filters.stage === s.key ? "bg-[#002FA7]/5 border-b-2 border-b-[#002FA7]" : "bg-white hover:bg-gray-50"}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColors[s.key] }} />
              <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{s.label}</span>
            </div>
            <p className="text-xl font-bold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.count || 0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-frn-status" placeholder="Search FRN, client, provider..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status_471 || "all"} onValueChange={(v) => setFilter("status_471", v)}>
          <SelectTrigger data-testid="filter-471-status" className="rounded-none w-[130px]"><SelectValue placeholder="471 Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All 471</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="committed">Committed</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={filters.pia_status || "all"} onValueChange={(v) => setFilter("pia_status", v)}>
          <SelectTrigger data-testid="filter-pia" className="rounded-none w-[140px]"><SelectValue placeholder="PIA Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All PIA</SelectItem><SelectItem value="not_started">Not Started</SelectItem><SelectItem value="in_review">In Review</SelectItem><SelectItem value="info_requested">Info Req.</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={filters.category || "all"} onValueChange={(v) => setFilter("category", v)}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Cat</SelectItem><SelectItem value="category_1">Cat 1</SelectItem><SelectItem value="category_2">Cat 2</SelectItem></SelectContent>
        </Select>
        <Select value={filters.funding_year || "all"} onValueChange={(v) => setFilter("funding_year", v)}>
          <SelectTrigger data-testid="filter-frn-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {[...new Set(items.map((i) => i.funding_year).filter(Boolean))].sort().reverse().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear filters</button>}
        <span className="ml-auto text-xs text-[#6B7280]">{processed.length} FRNs</span>
      </div>

      {/* Table */}
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="frn">FRN</SH>
              <SH k="client_name">Client</SH>
              <SH k="funding_year">FY</SH>
              <SH k="service_provider">Provider</SH>
              <SH k="requested">Requested</SH>
              <SH k="status_471">471 Status</SH>
              <SH k="pia_status">PIA</SH>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-center">486</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-center">472</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-center">474</TableHead>
              <SH k="total_disbursed">Disbursed</SH>
              <SH k="pct_disbursed">% Paid</SH>
              <SH k="stage">Stage</SH>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={14} className="text-center py-8 text-[#6B7280] text-sm">No FRN records found</TableCell></TableRow>
            ) : processed.map((r, idx) => (
              <TableRow key={`${r.frn}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`frn-row-${r.frn}`}>
                <TableCell className="py-2.5 px-3 font-mono text-xs font-bold text-[#002FA7]">{r.frn}</TableCell>
                <TableCell className="py-2.5 px-3 text-xs text-[#1A1D20] font-medium max-w-[150px] truncate">{r.client_name || "—"}</TableCell>
                <TableCell className="py-2.5 px-3 text-xs">{r.funding_year}</TableCell>
                <TableCell className="py-2.5 px-3 text-xs text-[#6B7280] max-w-[120px] truncate">{r.service_provider || "—"}</TableCell>
                <TableCell className="py-2.5 px-3 text-xs font-medium">{fmt(r.requested)}</TableCell>
                <TableCell className="py-2.5 px-3">
                  <Badge className={`rounded-none text-[9px] border capitalize ${statusColors[r.status_471] || statusColors.draft}`}>{r.status_471}</Badge>
                </TableCell>
                <TableCell className="py-2.5 px-3">
                  <Badge className={`rounded-none text-[9px] border ${piaColors[r.pia_status] || piaColors.not_started}`}>{piaLabels[r.pia_status] || r.pia_status}</Badge>
                </TableCell>
                <TableCell className="py-2.5 px-3 text-center"><FiledIcon filed={r.has_486} /></TableCell>
                <TableCell className="py-2.5 px-3 text-center"><FiledIcon filed={r.has_472} /></TableCell>
                <TableCell className="py-2.5 px-3 text-center"><FiledIcon filed={r.has_474} /></TableCell>
                <TableCell className="py-2.5 px-3 text-xs font-medium text-[#00B341]">{r.total_disbursed > 0 ? fmt(r.total_disbursed) : "—"}</TableCell>
                <TableCell className="py-2.5 px-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-10 h-1.5 bg-gray-100"><div className="h-full bg-[#00B341]" style={{ width: `${Math.min(r.pct_disbursed, 100)}%` }} /></div>
                    <span className="text-[10px] text-[#6B7280]">{r.pct_disbursed}%</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5 px-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stageColors[r.stage] || "#6B7280" }} />
                    <span className="text-[10px] font-medium text-[#1A1D20]">{stageLabels[r.stage] || r.stage}</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5 px-3">
                  <button data-testid={`view-frn-detail-${r.frn}`} onClick={() => setDetail(r)} className="p-1 hover:bg-gray-100 transition-colors">
                    <ArrowRight size={12} className="text-[#6B7280]" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FRN {detail.frn} — Detail</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-4">
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold block">Client</span><p className="text-sm font-medium text-[#1A1D20]">{detail.client_name || "—"}</p></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold block">FY / Category</span><p className="text-sm text-[#1A1D20]">FY{detail.funding_year} / {detail.category === "category_1" ? "Cat 1" : "Cat 2"}</p></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold block">Provider / SPIN</span><p className="text-sm text-[#1A1D20]">{detail.service_provider} {detail.spin && `(${detail.spin})`}</p></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold block">Application #</span><p className="text-sm font-mono text-[#1A1D20]">{detail.application_number || "—"}</p></div>
                </div>

                {/* Funding */}
                <div className="border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-3">Funding</p>
                  <div className="grid grid-cols-4 gap-3">
                    <div><span className="text-[10px] text-[#6B7280]">Requested</span><p className="text-sm font-bold">{fmt(detail.requested)}</p></div>
                    <div><span className="text-[10px] text-[#6B7280]">Discount</span><p className="text-sm font-bold">{detail.discount_rate}%</p></div>
                    <div><span className="text-[10px] text-[#6B7280]">Invoiced</span><p className="text-sm font-bold text-[#D97706]">{fmt(detail.total_invoiced)}</p></div>
                    <div><span className="text-[10px] text-[#6B7280]">Disbursed</span><p className="text-sm font-bold text-[#00B341]">{fmt(detail.total_disbursed)}</p></div>
                  </div>
                  {detail.requested > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-[10px] text-[#6B7280] mb-1"><span>Disbursement progress</span><span>{detail.pct_disbursed}%</span></div>
                      <div className="w-full h-2 bg-gray-100"><div className="h-full bg-[#00B341]" style={{ width: `${Math.min(detail.pct_disbursed, 100)}%` }} /></div>
                    </div>
                  )}
                </div>

                {/* Form status pipeline */}
                <div className="border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-3">E-Rate Form Status</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div className="flex items-center gap-3"><span className="text-xs font-semibold text-[#1A1D20] w-16">Form 471</span><Badge className={`rounded-none text-[9px] border capitalize ${statusColors[detail.status_471] || ""}`}>{detail.status_471}</Badge></div>
                      <Badge className={`rounded-none text-[9px] border ${piaColors[detail.pia_status] || ""}`}>PIA: {piaLabels[detail.pia_status] || detail.pia_status}</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div className="flex items-center gap-3"><span className="text-xs font-semibold text-[#1A1D20] w-16">Form 486</span>{detail.has_486 ? <Badge className="rounded-none text-[9px] border bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20 capitalize">{detail.status_486 || "Filed"}</Badge> : <span className="text-xs text-[#6B7280]">Not filed</span>}</div>
                      {detail.service_start && <span className="text-xs text-[#6B7280]">Service: {detail.service_start}</span>}
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div className="flex items-center gap-3"><span className="text-xs font-semibold text-[#1A1D20] w-16">Form 472</span>{detail.has_472 ? <Badge className="rounded-none text-[9px] border bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20">{detail.bear_count} BEAR</Badge> : <span className="text-xs text-[#6B7280]">No BEAR invoices</span>}</div>
                      {detail.bear_disbursed > 0 && <span className="text-xs text-[#00B341] font-medium">{fmt(detail.bear_disbursed)}</span>}
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3"><span className="text-xs font-semibold text-[#1A1D20] w-16">Form 474</span>{detail.has_474 ? <Badge className="rounded-none text-[9px] border bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20">{detail.spi_count} SPI</Badge> : <span className="text-xs text-[#6B7280]">No SPI invoices</span>}</div>
                      {detail.spi_disbursed > 0 && <span className="text-xs text-[#00B341] font-medium">{fmt(detail.spi_disbursed)}</span>}
                    </div>
                  </div>
                </div>

                {/* FCDL */}
                {detail.fcdl_date && (
                  <div className="text-xs text-[#6B7280]">FCDL Date: <span className="font-medium text-[#1A1D20]">{detail.fcdl_date}</span></div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
